import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wallet, LogIn, LogOut, Link2, Loader2, CheckCircle, ExternalLink, AlertCircle } from 'lucide-react';
import { ethers } from 'ethers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Web3AuthCard = ({ token, onWalletLinked, linked }) => {
  const [loading, setLoading] = useState(false);
  const [walletStatus, setWalletStatus] = useState(null);
  const [connectedAddress, setConnectedAddress] = useState(null);
  const [linking, setLinking] = useState(false);
  const [hasMetaMask, setHasMetaMask] = useState(false);

  // Check for wallet provider
  useEffect(() => {
    setHasMetaMask(typeof window.ethereum !== 'undefined');
    
    // Check if already connected
    if (window.ethereum) {
      window.ethereum.request({ method: 'eth_accounts' })
        .then(accounts => {
          if (accounts.length > 0) {
            setConnectedAddress(accounts[0]);
          }
        })
        .catch(console.error);
      
      // Listen for account changes
      window.ethereum.on('accountsChanged', (accounts) => {
        setConnectedAddress(accounts[0] || null);
      });
    }
  }, []);

  // Load wallet status from backend
  useEffect(() => {
    const loadStatus = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/pas-web3auth/wallet-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWalletStatus(res.data);
      } catch (err) {
        console.error('Failed to load wallet status:', err);
      }
    };
    loadStatus();
  }, [token, linked?.address]);

  const connect = async () => {
    if (!window.ethereum) {
      toast.error('Please install MetaMask or another Web3 wallet');
      window.open('https://metamask.io/download/', '_blank');
      return;
    }

    setLoading(true);
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      setConnectedAddress(accounts[0]);
      toast.success('Wallet connected!');
    } catch (error) {
      console.error('Connect error:', error);
      if (error.code === 4001) {
        toast.error('Connection rejected by user');
      } else {
        toast.error('Failed to connect wallet');
      }
    } finally {
      setLoading(false);
    }
  };

  const disconnect = () => {
    setConnectedAddress(null);
    toast.success('Wallet disconnected');
  };

  const linkWallet = async () => {
    if (!connectedAddress || !token) return;

    setLinking(true);
    try {
      const res = await axios.post(`${API}/pas-web3auth/link-wallet`,
        { address: connectedAddress },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setWalletStatus({ has_wallet: true, address: connectedAddress, source: 'metamask' });
      onWalletLinked?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link wallet');
    } finally {
      setLinking(false);
    }
  };

  const formatAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden" data-testid="web3auth-card">
      <div className="bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
            <svg viewBox="0 0 35 33" className="w-6 h-6">
              <path fill="#E2761B" d="M32.96 1l-13.14 9.72 2.45-5.73L32.96 1z"/>
              <path fill="#E4761B" d="M2.66 1l13.02 9.82-2.33-5.83L2.66 1zm25.57 22.53l-3.5 5.34 7.49 2.06 2.14-7.28-6.13-.12zm-26.96.12l2.13 7.28 7.47-2.06-3.48-5.34-6.12.12z"/>
              <path fill="#E4761B" d="M10.47 14.51l-2.08 3.14 7.4.34-.26-7.97-5.06 4.49zm14.68 0l-5.16-4.59-.17 8.07 7.4-.34-2.07-3.14zM10.87 28.87l4.49-2.16-3.88-3.03-.61 5.19zm9.4-2.16l4.51 2.16-.63-5.19-3.88 3.03z"/>
              <path fill="#F6851B" d="M24.78 28.87l-4.51-2.16.36 2.95-.04 1.24 4.19-2.03zm-13.91 0l4.2 2.03-.03-1.24.34-2.95-4.51 2.16z"/>
              <path fill="#C0AD9E" d="M15.12 21.78l-3.74-1.1 2.64-1.21 1.1 2.31zm5.38 0l1.1-2.31 2.65 1.21-3.75 1.1z"/>
              <path fill="#161616" d="M10.87 28.87l.65-5.34-4.13.12 3.48 5.22zm13.28-5.34l.63 5.34 3.5-5.22-4.13-.12zm3.18-5.31l-7.4.34.69 3.82 1.1-2.31 2.65 1.21 2.96-3.06zM11.38 20.68l2.64-1.21 1.1 2.31.69-3.82-7.4-.34 2.97 3.06z"/>
              <path fill="#763D16" d="M8.41 17.62l3.05 5.94-.1-2.96-2.95-2.98zm15.14 2.98l-.12 2.96 3.07-5.94-2.95 2.98zm-7.74-2.64l-.69 3.82.87 4.46.2-5.87-.38-2.41zm4 0l-.36 2.39.18 5.89.87-4.46-.69-3.82z"/>
              <path fill="#E2761B" d="M20.5 21.78l-.87 4.46.62.44 3.88-3.03.12-2.96-3.75 1.09zm-9.12-1.1l.1 2.96 3.88 3.03.62-.44-.87-4.46-3.73-1.09z"/>
              <path fill="#C0AD9E" d="M20.59 30.94l.04-1.24-.34-.29h-5.04l-.32.29.03 1.24-4.2-2.03 1.47 1.2 2.98 2.07h5.12l3-2.07 1.46-1.2-4.2 2.03z"/>
              <path fill="#161616" d="M20.27 26.71l-.62-.44h-3.68l-.62.44-.34 2.95.32-.29h5.04l.34.29-.44-2.95z"/>
              <path fill="#763D16" d="M33.52 11.35l1.11-5.36L32.96 1l-12.69 9.4 4.88 4.12 6.89 2.01 1.52-1.78-.66-.48 1.05-.96-.81-.62 1.05-.8-.69-.52zM.98 6l1.11 5.35-.71.52 1.05.8-.8.62 1.05.96-.66.48 1.51 1.78 6.89-2.01 4.88-4.12L2.66 1 .98 6z"/>
              <path fill="#F6851B" d="M32.04 16.53l-6.89-2.01 2.08 3.14-3.07 5.94 4.04-.05h6.07l-2.23-7.02zM10.47 14.52l-6.89 2.01-2.3 7.02h6.07l4.02.05-3.05-5.94 2.15-3.14zm9.34 3.44l.44-7.59 2-5.41h-8.87l1.98 5.41.45 7.59.17 2.43.02 5.85h3.68l.03-5.85.1-2.43z"/>
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Connect Wallet</h3>
            <p className="text-sm text-orange-100">MetaMask or Browser Wallet</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Current BETH Wallet Status */}
        {walletStatus?.has_wallet && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
            <div className="flex items-center gap-2 text-emerald-400 mb-2">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">MetaMask Wallet Linked</span>
            </div>
            <p className="text-xs text-emerald-300 font-mono truncate">{walletStatus.address}</p>
            <p className="text-xs text-slate-400 mt-1">Source: {walletStatus.source}</p>
          </div>
        )}

        {/* No MetaMask Warning */}
        {!hasMetaMask && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-sm text-amber-200 font-medium">Web3 Wallet Required</p>
              <p className="text-xs text-amber-300 mt-1">
                Install MetaMask or another Web3 wallet to connect and deploy Treasury Checks.
              </p>
              <a href="https://metamask.io/download/" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 mt-2">
                Get MetaMask <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}

        {/* Connection UI */}
        {!connectedAddress ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Connect your MetaMask or browser wallet to fund gas and deploy Treasury Checks on Ethereum.
            </p>
            <button onClick={connect}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium disabled:opacity-50"
              data-testid="web3auth-connect-btn">
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Connecting...</>
              ) : (
                <><LogIn className="w-5 h-5" /> Connect Wallet</>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Connected State */}
            <div className="bg-slate-700/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Connected Wallet</span>
                <button onClick={disconnect}
                  className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                  data-testid="web3auth-disconnect-btn">
                  <LogOut className="w-3 h-3" /> Disconnect
                </button>
              </div>

              <div className="flex items-center gap-2">
                <code className="text-sm text-orange-400 font-mono">{formatAddress(connectedAddress)}</code>
                <a href={`https://etherscan.io/address/${connectedAddress}`}
                  target="_blank" rel="noopener noreferrer"
                  className="p-1 text-slate-400 hover:text-white">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Link to BETH Account */}
            {(!walletStatus?.has_wallet || walletStatus.address?.toLowerCase() !== connectedAddress?.toLowerCase()) && (
              <button onClick={linkWallet}
                disabled={linking}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium disabled:opacity-50"
                data-testid="web3auth-link-btn">
                {linking ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Linking...</>
                ) : (
                  <><Link2 className="w-5 h-5" /> Link to BETH Account</>
                )}
              </button>
            )}

            {walletStatus?.has_wallet && walletStatus.address?.toLowerCase() === connectedAddress?.toLowerCase() && (
              <div className="text-center text-sm text-emerald-400">
                <CheckCircle className="w-4 h-4 inline mr-1" />
                Wallet linked to your BETH account
              </div>
            )}
          </div>
        )}

        {/* Info */}
        <div className="text-xs text-slate-500 border-t border-slate-700 pt-4">
          <p>Connect your MetaMask wallet to deploy Treasury Checks on Ethereum mainnet or Sepolia testnet.</p>
        </div>
      </div>
    </div>
  );
};

export default Web3AuthCard;
